import HBIcon from "@/components/HBIcon";
import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { Button } from "antd";
import { useRouter } from "next/router";
import { CSSProperties, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PinButton from "./button";
import PinDownload from "./download";
import PinEdit from "./edit";
import styles from "./index.module.less";
import PinLike from "./like";
import PinSource from "./source";

export function usePinActionsWithCard() {
  const [pinActionsProps, setPinActionsProps] = useState<
    Partial<IPinActionsProps>
  >({});

  /**
   * 鼠标进入卡片
   * 获取卡片图片dom的位置信息与宽高、offsetParent，用于定位操作按钮
   * pin: 当前卡片的采集信息，用于采集按钮操作
   */
  const onMouseEnterCard = useCallback(({ pin, imageDom, onUpdatePin }) => {
    setPinActionsProps({
      pin,
      position: {
        x: imageDom.offsetLeft,
        y: imageDom.offsetTop,
      },
      width: imageDom.offsetWidth,
      height: imageDom.offsetHeight,
      onUpdatePin,
      offsetParent: imageDom.offsetParent,
    });
  }, []);

  const onMouseLeaveCard = useCallback(() => {
    setPinActionsProps({});
  }, []);

  return {
    pinActionsProps,
    onMouseEnterCard,
    onMouseLeaveCard,
  };
}

interface IPinActionsProps {
  pin?: IPin;
  position?: {
    x: number;
    y: number;
  };
  width?: number;
  height?: number;
  onUpdatePin: (pin: IPin) => void;
  offsetParent?: HTMLElement;
  fullWidth?: boolean;
  showDownload?: boolean;
}

/**
 * 采集卡片操作按钮组件
 * 定位原理：使用绝对定位，根据图片的offset位置，计算出按钮的位置，将组件渲染到卡片组件的offsetParent中
 * @returns
 */
export default function PinActions({
  pin,
  position,
  width = 0,
  height = 0,
  onUpdatePin,
  offsetParent,
  fullWidth,
  showDownload,
}: IPinActionsProps) {
  position = position || {
    x: -9999,
    y: -9999,
  };

  const { user, vips, refreshVips } = useAppStore(
    ({ user, vips, refreshVips }) => ({ user, vips, refreshVips })
  );

  return createPortal(
    <>
      {/* 左上角 */}
      <PositionWrap
        position={position}
        offset={{
          x: 4,
          y: 12,
        }}
        offsetParent={offsetParent}
      >
        {pin && <PinButton pin={pin} current={!!pin} />}
        {fullWidth && (
          <PinLike
            pin={pin}
            style={{ marginLeft: 8 }}
            onUpdatePin={onUpdatePin}
          />
        )}
      </PositionWrap>

      {/* 右上角 */}
      <PositionWrap
        position={position}
        offset={{
          x: 12 - width,
          y: 12,
        }}
        right
        offsetParent={offsetParent}
      >
        {(pin?.user?.user_id || pin?.user_id) === user?.user_id ? (
          <PinEdit pin={pin} onUpdatePin={onUpdatePin} />
        ) : (
          !fullWidth && <PinLike pin={pin} onUpdatePin={onUpdatePin} />
        )}
        {fullWidth && (
          <Button
            className={styles.btn}
            data-gd-click="button_click"
            data-button-name="搜索相似采集"
            href={`/pins/${pin?.pin_id}/similar`}
            target="_blank"
            rel="nofollow"
            // title="搜索相似采集"
            data-tooltip="搜相似"
            data-tooltip-position="bottom"
          >
            <HBIcon type="ic_search-similar1" />
          </Button>
        )}
      </PositionWrap>

      {/* 左下角 */}
      {!fullWidth && (
        <PositionWrap
          position={position}
          offset={{
            x: 12,
            y: 12 - height,
          }}
          bottom
          offsetParent={offsetParent}
        >
          {pin?.source_type && <PinSource pin={pin} />}
          <Button
            className={styles.btn}
            data-gd-click="button_click"
            data-button-name="搜索相似采集"
            href={`/pins/${pin?.pin_id}/similar`}
            target="_blank"
            rel="nofollow"
            // title="搜索相似采集"
            data-tooltip="搜相似"
            data-tooltip-position="bottom"
          >
            <HBIcon type="ic_search-similar1" />
          </Button>
          {showDownload ? (
            <PinDownload
              pin={pin}
              user={user}
              vips={vips}
              refreshVips={refreshVips}
            />
          ) : null}
        </PositionWrap>
      )}
    </>,
    offsetParent || document.body
  );
}

/**
 * 定位包裹组件
 * */
function PositionWrap({
  children,
  offset,
  position,
  right = false,
  bottom = false,
  offsetParent,
}: {
  children: React.ReactNode;
  offset?: {
    x: number;
    y: number;
  };
  position: {
    x: number;
    y: number;
  };
  right?: boolean;
  bottom?: boolean;
  offsetParent?: HTMLElement;
}) {
  /** 偏移量 */
  offset = offset || {
    x: 0,
    y: 0,
  };
  const router = useRouter();
  const operate = () => {
    // 关注页面需要记录用户有没有操作
    if (router.pathname === "/follow") {
      window.dispatchEvent(new Event(EVENT.CARD_OPERATE));
    }
  };
  // 计算定位信息
  const style = useMemo<CSSProperties>(() => {
    const obj = {
      opacity: position.x === -9999 ? 0 : 1,
    };

    if (right) {
      Object.assign(obj, {
        right:
          (offsetParent || document.body).clientWidth - position.x + offset.x,
      });
    } else {
      Object.assign(obj, {
        left: position.x + offset.x,
      });
    }

    if (bottom) {
      Object.assign(obj, {
        bottom:
          (offsetParent || document.body).clientHeight - position.y + offset.y,
      });
    } else {
      Object.assign(obj, {
        top: position.y + offset.y,
      });
    }

    return obj;
  }, [bottom, offset.x, offset.y, offsetParent, position.x, position.y, right]);

  return (
    <div className={styles.actionWrap} style={style} onClick={operate}>
      {children}
    </div>
  );
}
